// Libs
import { Avatar, Box, FormHelperText, Stack, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { DropzoneProps, DropzoneRef, useDropzone } from 'react-dropzone';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { IFileUpload } from '~/utils/interface/common';
import { FILE_UPLOAD_PROP_MIN_FILE, FILE_UPLOAD_PROP_MAX_FILE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './UploadDocument.module.scss';
import { icons } from '~/assets';

export type UploadDocumentProps = DropzoneProps &
  React.RefAttributes<DropzoneRef> & {
    onChange?: (files: IFileUpload[]) => void;
    uploadedFiles?: IFileUpload[];
    isMultiple?: boolean;
    errorMessage?: string;
  };

const cx = classNames.bind(styles);

const UploadDocument = (props: UploadDocumentProps) => {
  //#region Destructuring Props
  const {
    isMultiple = true,
    maxFiles = isMultiple ? FILE_UPLOAD_PROP_MAX_FILE : FILE_UPLOAD_PROP_MIN_FILE,
    errorMessage,
    ...restProps
  } = props;
  delete restProps.onChange;
  //#endregion Destructuring Props
  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    multiple: isMultiple,
    ...restProps,
    onDrop: (acceptedFiles: Array<File>) => {
      const updatedFiles = [
        ...uploadFiles,
        ...acceptedFiles.map((item) => ({
          file: item,
          name: item.name,
        })),
      ];
      setUploadFiles(updatedFiles);

      if (props.onChange) {
        props.onChange(updatedFiles);
        setUploadFiles([]);
      }
    },
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [uploadFiles, setUploadFiles] = useState<IFileUpload[]>([]);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props?.uploadedFiles?.length) {
      setUploadFiles([...props.uploadedFiles.filter((uploadedFile) => !!uploadedFile?.url)]);
    }
  }, [props?.uploadedFiles]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function
  return (
    <Box>
      {uploadFiles.length < maxFiles && (
        <Box className={cx('container')} {...getRootProps()}>
          <input {...getInputProps()} />
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={'10px'}
            flexWrap={'wrap'}
          >
            <Avatar src={icons.commonIconFileUpload} alt={t('common_alt_img', { ns: 'common/base' })} />

            <Typography component={'p'} className={cx('placeholder')}>
              {t('component_document_upload_text_btn')}
              <Typography component={'span'} className={cx('placeholderBrowse')}>
                {t('component_document_upload_browse_btn')}
              </Typography>
            </Typography>
          </Stack>
        </Box>
      )}
      {errorMessage && <FormHelperText className={cx('errMessage')}>{errorMessage}</FormHelperText>}
    </Box>
  );
};
export default memo(UploadDocument);
